import React, { forwardRef, useImperativeHandle } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { TextFieldCustom } from '../helpers/formFields';

import { formatNumberThsndAndDec } from '../helpers/stringFormat';
import { startAdornmentCurrency, endAdornmentCurrencyUnitFrequency, addCurrency } from '../helpers/currencyFormat';

const vanWestendorpQuestions = {
    too_expensive: 'At what price would you consider the product so expensive that you would not consider buying it?',
    expensive: 'At what price would you consider the product to get expensive, but you would still consider buying it?',
    bargain: 'At what price would you consider the product to be a bargain - a great buy for the money?',
    too_cheap: 'At what price would you consider the product to be priced so low that you would doubt the quality and not consider it?'
}

const isNumeric = (value) => {
    return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
};

const isPositive = (value) => {
    return parseFloat(value.toString().replace(/,/g, '')) > 0;
};

const isLarger = (value1, value2) => {
    if (!value1 || !value2) {
        return false
    }
    return parseFloat(value1.toString().replace(/,/g, '')) >= parseFloat(value2.toString().replace(/,/g, ''));
};

const VanWestendorpQuestionsForm = forwardRef(({ respondentId, vanWestendorp, formActions, onSubmit }, ref) => {
    const initialValues = {
        id: vanWestendorp.id,
        respondent_id: respondentId,
        currency: vanWestendorp.currency,
        frequency: vanWestendorp.frequency,
        unit: vanWestendorp.unit,
        too_expensive: '',
        expensive: '',
        bargain: '',
        too_cheap: '',
    }

    const validate = (values) => {
        const errors = {};
        if (!values.too_expensive) {
            errors.too_expensive = 'Price is needed'
        } else if (!isNumeric(values.too_expensive)) {
            errors.too_expensive = 'Must be numeric value'
        } else if (!isPositive(values.too_expensive)) {
            errors.too_expensive = 'Must be positive'
        }

        if (!values.expensive) {
            errors.expensive = 'Price is needed'
        } else if (!isNumeric(values.expensive)) {
            errors.expensive = 'Must be numeric value'
        } else if (!isPositive(values.expensive)) {
            errors.expensive = 'Must be positive'
        }

        if (!values.bargain) {
            errors.bargain = 'Price is needed'
        } else if (!isNumeric(values.bargain)) {
            errors.bargain = 'Must be numeric value'
        } else if (!isPositive(values.bargain)) {
            errors.bargain = 'Must be positive'
        }

        if (!values.too_cheap) {
            errors.too_cheap = 'Price is needed'
        } else if (!isNumeric(values.too_cheap)) {
            errors.too_cheap = 'Must be numeric value'
        } else if (!isPositive(values.too_cheap)) {
            errors.too_cheap = 'Must be positive'
        }

        if (vanWestendorp.price_range) {
            if (!isLarger(values.too_expensive, vanWestendorp.lower_price)) {
                errors.too_expensive = 'Must be in range'
            }
            if (!isLarger(vanWestendorp.upper_price, values.too_expensive)) {
                errors.too_expensive = 'Must be in range'
            }

            if (!isLarger(values.expensive, vanWestendorp.lower_price)) {
                errors.expensive = 'Must be in range'
            }
            if (!isLarger(vanWestendorp.upper_price, values.expensive)) {
                errors.expensive = 'Must be in range'
            }

            if (!isLarger(values.bargain, vanWestendorp.lower_price)) {
                errors.bargain = 'Must be in range'
            }
            if (!isLarger(vanWestendorp.upper_price, values.bargain)) {
                errors.bargain = 'Must be in range'
            }

            if (!isLarger(values.too_cheap, vanWestendorp.lower_price)) {
                errors.too_cheap = 'Must be in range'
            }
            if (!isLarger(vanWestendorp.upper_price, values.too_cheap)) {
                errors.too_cheap = 'Must be in range'
            }
        }
        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
        },
    }));

    const priceRangeText = () => {
        if (vanWestendorp.price_range) {
            return ` (Please answer in the range of ${addCurrency(vanWestendorp.lower_price, vanWestendorp.currency)} - ${addCurrency(vanWestendorp.upper_price, vanWestendorp.currency)})`
        }
        else {
            return ''
        }
    }

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 2 }}>
            <Grid container component="main" columnSpacing={1} rowSpacing={3}>
                <Grid item xs={8}>
                    <Typography variant="body2">
                        {vanWestendorpQuestions.too_expensive}{priceRangeText()}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <TextFieldCustom
                        name="too_expensive"
                        label="Too expensive"
                        InputLabelProps={{ shrink: true }}
                        startAdornment={startAdornmentCurrency(vanWestendorp.currency)}
                        endAdornment={endAdornmentCurrencyUnitFrequency(vanWestendorp.currency, vanWestendorp.unit, vanWestendorp.frequency)}
                        value={formik.values.too_expensive}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue('too_expensive', formattedValue || '')
                        }}
                        // sx={{ maxWidth: 300 }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, 'too_expensive') && Boolean(getIn(formik.errors, 'too_expensive'))}
                        helperText={getIn(formik.touched, 'too_expensive') && getIn(formik.errors, 'too_expensive')}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Typography variant="body2">
                        {vanWestendorpQuestions.expensive}{priceRangeText()}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <TextFieldCustom
                        name="expensive"
                        label="Expensive"
                        InputLabelProps={{ shrink: true }}
                        startAdornment={startAdornmentCurrency(vanWestendorp.currency)}
                        endAdornment={endAdornmentCurrencyUnitFrequency(vanWestendorp.currency, vanWestendorp.unit, vanWestendorp.frequency)}
                        value={formik.values.expensive}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue('expensive', formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, 'expensive') && Boolean(getIn(formik.errors, 'expensive'))}
                        helperText={getIn(formik.touched, 'expensive') && getIn(formik.errors, 'expensive')}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Typography variant="body2">
                        {vanWestendorpQuestions.bargain}{priceRangeText()}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <TextFieldCustom
                        name="bargain"
                        label="A bargain"
                        InputLabelProps={{ shrink: true }}
                        startAdornment={startAdornmentCurrency(vanWestendorp.currency)}
                        endAdornment={endAdornmentCurrencyUnitFrequency(vanWestendorp.currency, vanWestendorp.unit, vanWestendorp.frequency)}
                        value={formik.values.bargain}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue('bargain', formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, 'bargain') && Boolean(getIn(formik.errors, 'bargain'))}
                        helperText={getIn(formik.touched, 'bargain') && getIn(formik.errors, 'bargain')}
                    />
                </Grid>
                <Grid item xs={8}>
                    <Typography variant="body2">
                        {vanWestendorpQuestions.too_cheap}{priceRangeText()}
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <TextFieldCustom
                        name="too_cheap"
                        label="Too cheap"
                        InputLabelProps={{ shrink: true }}
                        startAdornment={startAdornmentCurrency(vanWestendorp.currency)}
                        endAdornment={endAdornmentCurrencyUnitFrequency(vanWestendorp.currency, vanWestendorp.unit, vanWestendorp.frequency)}
                        value={formik.values.too_cheap}
                        onChange={(event) => {
                            const { value } = event.target
                            const formattedValue = formatNumberThsndAndDec(value)
                            formik.setFieldValue('too_cheap', formattedValue || '')
                        }}
                        onBlur={formik.handleBlur}
                        error={getIn(formik.errors, 'too_cheap') && Boolean(getIn(formik.errors, 'too_cheap'))}
                        helperText={getIn(formik.touched, 'too_cheap') && getIn(formik.errors, 'too_cheap')}
                    />
                </Grid>
            </Grid>
            {/* <Divider sx={{ mt: 5 }} /> */}
            {formActions()}
        </Box>
    );

})

export default VanWestendorpQuestionsForm